/**
 * Plan intent — carrying "I clicked Choose Pro" from the marketing site all the
 * way to Stripe Checkout.
 *
 * The problem this solves: every marketing pricing CTA (Get started free /
 * Choose Pro / Choose Unlimited, the last two advertised with a 7-day trial)
 * linked to the same bare /coach/sign-up. Nothing recorded which one was
 * clicked, so a coach with buying intent landed on the free tier and had to
 * rediscover /upgrade on their own. Their intent was dropped at the door.
 *
 * Where the intent lives, and why in two places:
 * - `intended_plan` on the Supabase auth user_metadata is the durable copy. It
 *   survives the email-confirmation round trip, which can finish in a different
 *   tab (or day) than the one the coach signed up in.
 * - localStorage is the fallback for Google OAuth, where the browser leaves the
 *   SPA entirely and comes back through /api/auth/callback, so nothing we set
 *   in React state survives.
 *
 * Both are read at the end of onboarding; whichever answers first wins, and the
 * localStorage copy is cleared as soon as it is consumed so a stale click from
 * last week can't hijack a later signup.
 */

export type PaidTier = 'pro' | 'unlimited';
export type BillingInterval = 'monthly' | 'annual';

export interface PlanIntent {
  tier: PaidTier;
  interval: BillingInterval;
}

const STORAGE_KEY = 'coachd:plan-intent';

/** Free is a valid choice — it just isn't an *intent* we need to carry anywhere. */
function toPaidTier(value: string | null | undefined): PaidTier | null {
  return value === 'pro' || value === 'unlimited' ? value : null;
}

function toInterval(value: string | null | undefined): BillingInterval {
  return value === 'annual' ? 'annual' : 'monthly';
}

/** Read `?plan=pro&interval=annual` off a URL. Unknown values are ignored, not guessed. */
export function readPlanIntentFromQuery(search: string): PlanIntent | null {
  const params = new URLSearchParams(search);
  const tier = toPaidTier(params.get('plan'));
  if (!tier) return null;
  return { tier, interval: toInterval(params.get('interval')) };
}

/** Survives the OAuth round trip, which leaves the SPA entirely. */
export function storePlanIntent(intent: PlanIntent): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    /* Safari private mode and friends — the user_metadata copy still applies. */
  }
}

/** Read and clear in one step: consuming it twice would re-trigger checkout. */
export function consumeStoredPlanIntent(): PlanIntent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PlanIntent>;
    const tier = toPaidTier(parsed?.tier);
    return tier ? { tier, interval: toInterval(parsed?.interval) } : null;
  } catch {
    return null;
  }
}

export function clearStoredPlanIntent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clean up */
  }
}

/** Shape written into auth user_metadata at signup, read back after onboarding. */
export function planIntentFromUserMetadata(meta: Record<string, unknown> | null | undefined): PlanIntent | null {
  const tier = toPaidTier(typeof meta?.intended_plan === 'string' ? meta.intended_plan : null);
  if (!tier) return null;
  const interval = typeof meta?.intended_interval === 'string' ? meta.intended_interval : null;
  return { tier, interval: toInterval(interval) };
}

/**
 * Trial days for a checkout. Only Pro gets one — create-checkout.ts rejects a
 * trial on any other tier with a 400, so sending one for Unlimited would turn
 * a purchase into an error page.
 */
export function trialDaysForTier(tier: PaidTier): number | undefined {
  if (tier !== 'pro') return undefined;
  const configured = Number((import.meta as any).env?.VITE_STRIPE_PRO_TRIAL_DAYS || 7);
  return Number.isFinite(configured) && configured > 0 ? configured : 7;
}
